import type { ProctorSpecification } from '../../shared/proctor/ProctorSpecification';

import { getProctorAllocation } from '../../shared/native-modules/tare/getProctorAllocation';

export const RnGhostwriterTest: ProctorSpecification = {
    name: 'rn_ghostwriter_test',
    buckets: {
        inactive: -1,
        control: 0,
        active: 1,
        phase_a: 2,
        phase_b: 3,
        phase_c: 4,
        phase_d: 5,
    },
};

const RN_GHOSTWRITER_ACTIVE_BUCKET = RnGhostwriterTest.buckets.active;
const RN_GHOSTWRITER_PHASE_A_BUCKET = RnGhostwriterTest.buckets.phase_a;
const RN_GHOSTWRITER_PHASE_B_BUCKET = RnGhostwriterTest.buckets.phase_b;
const RN_GHOSTWRITER_PHASE_C_BUCKET = RnGhostwriterTest.buckets.phase_c;
const RN_GHOSTWRITER_PHASE_D_BUCKET = RnGhostwriterTest.buckets.phase_d;

/**
 * Result of resolving the `phase_b` allocation.
 *
 * - `inBucket`: whether the user is actually allocated to (or forced into)
 *   the `phase_b` bucket.
 * - `jobKeys`: the list parsed from the proctor payload
 *   (`{ "jobKeys": "stringArray" }`). Empty when not in `phase_b`, when the
 *   payload is missing (e.g. bucket forced via debug menu, which doesn't
 *   carry a payload), or when the payload is present but malformed.
 *
 * Callers need both fields to distinguish "in `phase_b` with a usable
 * payload" (fast-path: skip backend eligibility) from "in `phase_b` but no
 * payload" (fall back to the normal eligibility flow).
 */
export interface RnGhostwriterPhaseBAllocation {
    inBucket: boolean;
    jobKeys: string[];
}

/**
 * Single source of truth for `rn_ghostwriter_test` state.
 *
 * Resolved from one bridge call so the three derived flags are always
 * mutually consistent — splitting this across multiple `getProctorAllocation`
 * calls would allow a transient bridge failure (or a mid-flight matrix
 * refresh) to persist an impossible combination like
 * `isActive && isPhaseB`.
 */
export interface RnGhostwriterAllocation {
    isActive: boolean;
    isPhaseA: boolean;
    /**
     * Phase C reuses phase A's Prefill synchronous UI flow, but keeps the
     * standard 'Auto apply' / 'Try auto apply' CTA copy (like active/phase_b)
     * rather than phase A's 'Prefill your application'. See RN-5091.
     */
    isPhaseC: boolean;
    /**
     * Phase D reuses phase A's Prefill synchronous UI flow and CTA copy
     * verbatim, and additionally surfaces the CAPTCHA / 2FA intervention
     * screen (the daemon's Browserless live URL) when a submission parks
     * in `AWAITING_USER_INTERVENTION`. See RN-3367.
     */
    isPhaseD: boolean;
    phaseB: RnGhostwriterPhaseBAllocation;
}

const EMPTY_ALLOCATION: RnGhostwriterAllocation = {
    isActive: false,
    isPhaseA: false,
    isPhaseC: false,
    isPhaseD: false,
    phaseB: { inBucket: false, jobKeys: [] },
};

export async function resolveRnGhostwriterAllocation(): Promise<RnGhostwriterAllocation> {
    const allocation = await getProctorAllocation(RnGhostwriterTest);
    if (allocation == null) return EMPTY_ALLOCATION;
    return {
        isActive: allocation.value === RN_GHOSTWRITER_ACTIVE_BUCKET,
        isPhaseA: allocation.value === RN_GHOSTWRITER_PHASE_A_BUCKET,
        isPhaseC: allocation.value === RN_GHOSTWRITER_PHASE_C_BUCKET,
        isPhaseD: allocation.value === RN_GHOSTWRITER_PHASE_D_BUCKET,
        phaseB:
            allocation.value === RN_GHOSTWRITER_PHASE_B_BUCKET
                ? { inBucket: true, jobKeys: parsePhaseBJobKeys(allocation.payload) }
                : { inBucket: false, jobKeys: [] },
    };
}

// The TARE bridge may deliver proctor payloads as multi-level JSON-encoded
// strings (Android in particular has historically double-encoded payloads —
// see `parseHpSectionProctorPayload`). Re-parse while still a string, capped
// to avoid pathological inputs.
const MAX_PAYLOAD_PARSE_DEPTH = 5;

/**
 * Tolerant of three observed payload shapes:
 *
 *  - Object: `{ jobKeys: ["…"] }` — what the spec (`type: "map"`) implies and
 *    what the production bridge now returns.
 *  - Bare array: `["…"]` — defensive; in case a future SDK / serializer
 *    change flattens the single-field map to its inner stringArray.
 *  - Either of the above, wrapped in 1–N layers of JSON string encoding.
 */
function parsePhaseBJobKeys(payload: unknown): string[] {
    if (payload == null || payload === '') return [];
    try {
        let parsed: unknown = payload;
        for (let i = 0; i < MAX_PAYLOAD_PARSE_DEPTH && typeof parsed === 'string'; i++) {
            parsed = JSON.parse(parsed);
        }
        if (Array.isArray(parsed)) {
            return parsed.filter((k): k is string => typeof k === 'string');
        }
        if (parsed != null && typeof parsed === 'object') {
            const jobKeys = (parsed as { jobKeys?: unknown }).jobKeys;
            if (Array.isArray(jobKeys)) {
                return jobKeys.filter((k): k is string => typeof k === 'string');
            }
        }
        return [];
    } catch {
        return [];
    }
}
